import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Truck, Package, MapPin, Thermometer, Clock, 
  FileText, Download, RefreshCw, Search, Eye,
  CheckCircle, AlertTriangle, XCircle, Loader2, TrendingUp, QrCode
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, Area, ComposedChart
} from 'recharts';

const statusColors = {
  planned: 'bg-slate-500',
  in_progress: 'bg-blue-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500',
};

const deliveryStatusColors = {
  pending: 'bg-slate-500',
  in_transit: 'bg-blue-500',
  delivered: 'bg-green-500',
  failed: 'bg-red-500',
  returned: 'bg-orange-500',
};

const complianceStatusIcons = {
  compliant: { icon: CheckCircle, color: 'text-green-500' },
  non_compliant: { icon: XCircle, color: 'text-red-500' },
  partial: { icon: AlertTriangle, color: 'text-amber-500' },
  pending: { icon: Clock, color: 'text-slate-400' },
};

export default function TripsPage() {
  const { user, tenant } = useTrackOticAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(null);
  const [invoiceReports, setInvoiceReports] = useState({});
  const [invoiceTelemetry, setInvoiceTelemetry] = useState({});
  const [loadingTelemetry, setLoadingTelemetry] = useState({});
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  useEffect(() => {
    fetchTrips();
  }, [statusFilter]);

  const fetchTrips = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await opsApi.getTrips(params);
      setTrips(response.data || []);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (trip) => {
    setSelectedTrip(trip);
    setDetailsOpen(true);
    
    // Fetch existing reports for all invoices
    try {
      const response = await opsApi.getInvoiceReports({ trip_id: trip.id });
      const reports = {};
      (response.data || []).forEach(report => {
        reports[report.invoice_number] = report;
      });
      setInvoiceReports(reports);
    } catch (error) {
      console.error('Error fetching invoice reports:', error);
    }
  };

  const handleGenerateReport = async (tripInvoiceId, invoiceNumber) => {
    try {
      setGeneratingReport(invoiceNumber);
      const response = await opsApi.generateInvoiceReport(tripInvoiceId);
      toast.success(`Report generated: ${response.data.report_number}`, { duration: 2000 });
      
      // Refresh reports
      if (selectedTrip) {
        const reportsResponse = await opsApi.getInvoiceReports({ trip_id: selectedTrip.id });
        const reports = {};
        (reportsResponse.data || []).forEach(report => {
          reports[report.invoice_number] = report;
        });
        setInvoiceReports(reports);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report', { duration: 3000 });
    } finally {
      setGeneratingReport(null);
    }
  };

  const handleDownloadPDF = (reportId) => {
    const url = opsApi.downloadInvoiceReportPDF(reportId);
    window.open(url, '_blank');
  };

  const handleToggleGraph = async (invoiceId, tripInvoiceId) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
      return;
    }
    
    setExpandedInvoice(invoiceId);
    
    // Fetch telemetry if not already loaded
    if (!invoiceTelemetry[invoiceId]) {
      setLoadingTelemetry(prev => ({ ...prev, [invoiceId]: true }));
      try {
        const response = await opsApi.getInvoiceTelemetry(tripInvoiceId);
        setInvoiceTelemetry(prev => ({ ...prev, [invoiceId]: response.data }));
      } catch (error) {
        console.error('Error fetching telemetry:', error);
        toast.error('Failed to load temperature data');
      } finally {
        setLoadingTelemetry(prev => ({ ...prev, [invoiceId]: false }));
      }
    }
  };

  const filteredTrips = trips.filter(trip => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      trip.trip_number?.toLowerCase().includes(term) ||
      trip.driver_name?.toLowerCase().includes(term) ||
      trip.vehicle_number?.toLowerCase().includes(term) ||
      trip.invoices?.some(inv => 
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.customer_name?.toLowerCase().includes(term)
      )
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="trips-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Trips & Invoice Reports</h1>
          <p className="text-slate-400 mt-1">
            View trips and generate invoice-wise compliance reports
          </p>
        </div>
        <Button
          onClick={fetchTrips}
          variant="outline"
          className="border-slate-700 text-slate-300 hover:bg-slate-800"
          data-testid="refresh-trips-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Truck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{trips.length}</p>
                <p className="text-slate-400 text-sm">Total Trips</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Package className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {trips.reduce((sum, t) => sum + (t.invoices?.length || 0), 0)}
                </p>
                <p className="text-slate-400 text-sm">Total Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {trips.filter(t => t.status === 'completed').length}
                </p>
                <p className="text-slate-400 text-sm">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {trips.filter(t => t.status === 'in_progress').length}
                </p>
                <p className="text-slate-400 text-sm">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search trips, invoices, customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-700 text-white"
            data-testid="trip-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Trips Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-slate-800/50">
                <TableHead className="text-slate-400">Trip #</TableHead>
                <TableHead className="text-slate-400">Device</TableHead>
                <TableHead className="text-slate-400">Driver</TableHead>
                <TableHead className="text-slate-400">Vehicle</TableHead>
                <TableHead className="text-slate-400">Invoices</TableHead>
                <TableHead className="text-slate-400">Temp Range</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Started</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading trips...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredTrips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                    No trips found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTrips.map((trip) => (
                  <TableRow 
                    key={trip.id} 
                    className="border-slate-800 hover:bg-slate-800/50"
                    data-testid={`trip-row-${trip.trip_number}`}
                  >
                    <TableCell className="font-medium text-white">
                      {trip.trip_number}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {trip.device_uid || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {trip.driver_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {trip.vehicle_number || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-600 text-slate-300">
                        {trip.invoices?.length || 0} invoices
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      <span className="flex items-center gap-1">
                        <Thermometer className="w-4 h-4 text-blue-400" />
                        {trip.temp_min}°C - {trip.temp_max}°C
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[trip.status] || 'bg-slate-500'} text-white`}>
                        {trip.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {formatDate(trip.actual_start || trip.planned_start)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(trip)}
                          className="text-teal-400 hover:text-teal-300 hover:bg-slate-800"
                          data-testid={`view-trip-${trip.trip_number}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {trip.qr_code && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/verify/${trip.qr_code}`, '_blank')}
                            className="text-blue-400 hover:text-blue-300 hover:bg-slate-800"
                            data-testid={`qr-trip-${trip.trip_number}`}
                          >
                            <QrCode className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Trip Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-teal-400" />
              Trip Details: {selectedTrip?.trip_number}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View trip information and generate invoice-wise compliance reports
            </DialogDescription>
          </DialogHeader>

          {selectedTrip && (
            <Tabs defaultValue="invoices" className="mt-4">
              <TabsList className="bg-slate-800 border-slate-700">
                <TabsTrigger value="invoices" className="data-[state=active]:bg-teal-600">
                  Invoices ({selectedTrip.invoices?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="details" className="data-[state=active]:bg-teal-600">
                  Trip Details
                </TabsTrigger>
              </TabsList>

              <TabsContent value="invoices" className="mt-4 space-y-4">
                {selectedTrip.invoices?.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No invoices found for this trip
                  </div>
                ) : (
                  selectedTrip.invoices?.map((invoice, index) => {
                    const report = invoiceReports[invoice.invoice_number];
                    const ComplianceIcon = report 
                      ? complianceStatusIcons[report.compliance_status]?.icon || Clock
                      : Clock;
                    const complianceColor = report 
                      ? complianceStatusIcons[report.compliance_status]?.color || 'text-slate-400'
                      : 'text-slate-400';

                    return (
                      <Card key={invoice.invoice_id} className="bg-slate-800 border-slate-700">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-slate-700 text-slate-300">
                                  #{invoice.sequence_number}
                                </Badge>
                                <span className="font-semibold text-white">
                                  {invoice.invoice_number}
                                </span>
                                <Badge className={`${deliveryStatusColors[invoice.status]} text-white`}>
                                  {invoice.status}
                                </Badge>
                              </div>
                              <div className="text-slate-300">
                                <span className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-slate-400" />
                                  {invoice.customer_name}
                                </span>
                                {invoice.customer_address && (
                                  <p className="text-sm text-slate-400 ml-6">
                                    {invoice.customer_address}
                                  </p>
                                )}
                              </div>
                              {invoice.actual_delivery_at && (
                                <p className="text-sm text-slate-400">
                                  Delivered: {formatDate(invoice.actual_delivery_at)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {report ? (
                                <>
                                  <div className="flex items-center gap-1 mr-2">
                                    <ComplianceIcon className={`w-5 h-5 ${complianceColor}`} />
                                    <span className={`text-sm ${complianceColor}`}>
                                      {report.compliance_status?.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadPDF(report.id)}
                                    className="border-teal-600 text-teal-400 hover:bg-teal-600/20"
                                    data-testid={`download-pdf-${invoice.invoice_number}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    PDF
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleGenerateReport(invoice.id, invoice.invoice_number)}
                                  disabled={generatingReport === invoice.invoice_number || invoice.status === 'pending'}
                                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                  data-testid={`generate-report-${invoice.invoice_number}`}
                                >
                                  {generatingReport === invoice.invoice_number ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <FileText className="w-4 h-4 mr-1" />
                                      Generate Report
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          {report && (
                            <div className="mt-3 pt-3 border-t border-slate-700">
                              {/* Stats Row */}
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-400">Min Temp:</span>
                                  <span className="ml-2 text-white">
                                    {report.temp_min_observed?.toFixed(1) || 'N/A'}°C
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Max Temp:</span>
                                  <span className="ml-2 text-white">
                                    {report.temp_max_observed?.toFixed(1) || 'N/A'}°C
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Avg Temp:</span>
                                  <span className="ml-2 text-white">
                                    {report.temp_avg_observed?.toFixed(1) || 'N/A'}°C
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Breaches:</span>
                                  <span className={`ml-2 ${report.breach_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    {report.breach_count || 0}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Show Graph Button */}
                              <div className="mt-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleGraph(invoice.invoice_number, invoice.id)}
                                  className="text-blue-400 hover:text-blue-300 hover:bg-slate-700 w-full justify-center"
                                  data-testid={`toggle-graph-${invoice.invoice_number}`}
                                >
                                  <TrendingUp className="w-4 h-4 mr-2" />
                                  {expandedInvoice === invoice.invoice_number ? 'Hide Temperature Graph' : 'Show Temperature Graph'}
                                </Button>
                              </div>
                              
                              {/* Temperature Graph */}
                              {expandedInvoice === invoice.invoice_number && (
                                <div className="mt-4 p-4 bg-slate-900 rounded-lg">
                                  {loadingTelemetry[invoice.invoice_number] ? (
                                    <div className="flex items-center justify-center py-8">
                                      <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
                                      <span className="ml-2 text-slate-400">Loading temperature data...</span>
                                    </div>
                                  ) : invoiceTelemetry[invoice.invoice_number]?.data_points?.length > 0 ? (
                                    <>
                                      <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                                          <Thermometer className="w-4 h-4 text-blue-400" />
                                          Temperature History
                                        </h4>
                                        <div className="flex items-center gap-4 text-xs">
                                          <span className="flex items-center gap-1">
                                            <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                                            Temperature
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <div className="w-3 h-1 bg-green-500"></div>
                                            Min ({invoiceTelemetry[invoice.invoice_number]?.temp_min_allowed}°C)
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <div className="w-3 h-1 bg-red-500"></div>
                                            Max ({invoiceTelemetry[invoice.invoice_number]?.temp_max_allowed}°C)
                                          </span>
                                        </div>
                                      </div>
                                      <ResponsiveContainer width="100%" height={200}>
                                        <ComposedChart data={invoiceTelemetry[invoice.invoice_number].data_points}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                          <XAxis 
                                            dataKey="time" 
                                            stroke="#94a3b8" 
                                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                                            interval="preserveStartEnd"
                                          />
                                          <YAxis 
                                            stroke="#94a3b8" 
                                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                                            domain={['dataMin - 2', 'dataMax + 2']}
                                            tickFormatter={(val) => `${val}°C`}
                                          />
                                          <Tooltip
                                            contentStyle={{ 
                                              backgroundColor: '#1e293b', 
                                              border: '1px solid #334155',
                                              borderRadius: '8px',
                                              color: '#fff'
                                            }}
                                            formatter={(value, name) => [
                                              `${value?.toFixed(1)}°C`,
                                              name === 'temperature' ? 'Temperature' : name
                                            ]}
                                          />
                                          <ReferenceLine 
                                            y={invoiceTelemetry[invoice.invoice_number]?.temp_min_allowed} 
                                            stroke="#22c55e" 
                                            strokeDasharray="5 5"
                                            label={{ value: 'Min', position: 'left', fill: '#22c55e', fontSize: 10 }}
                                          />
                                          <ReferenceLine 
                                            y={invoiceTelemetry[invoice.invoice_number]?.temp_max_allowed} 
                                            stroke="#ef4444" 
                                            strokeDasharray="5 5"
                                            label={{ value: 'Max', position: 'left', fill: '#ef4444', fontSize: 10 }}
                                          />
                                          <Area
                                            type="monotone"
                                            dataKey="temperature"
                                            fill="url(#tempGradient)"
                                            stroke="none"
                                          />
                                          <Line 
                                            type="monotone" 
                                            dataKey="temperature" 
                                            stroke="#14b8a6" 
                                            strokeWidth={2}
                                            dot={(props) => {
                                              const { cx, cy, payload } = props;
                                              if (payload.is_breach) {
                                                return (
                                                  <circle 
                                                    cx={cx} 
                                                    cy={cy} 
                                                    r={4} 
                                                    fill="#ef4444" 
                                                    stroke="#fff"
                                                    strokeWidth={1}
                                                  />
                                                );
                                              }
                                              return null;
                                            }}
                                            activeDot={{ r: 6, fill: '#14b8a6', stroke: '#fff', strokeWidth: 2 }}
                                          />
                                          <defs>
                                            <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                                              <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                                              <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                                            </linearGradient>
                                          </defs>
                                        </ComposedChart>
                                      </ResponsiveContainer>
                                      <p className="text-xs text-slate-500 mt-2 text-center">
                                        {invoiceTelemetry[invoice.invoice_number].total_points} data points • Red dots indicate temperature breaches
                                      </p>
                                    </>
                                  ) : (
                                    <div className="text-center py-8 text-slate-400">
                                      No temperature data available for this invoice
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-400">Trip Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Trip Number:</span>
                        <span className="text-white">{selectedTrip.trip_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Status:</span>
                        <Badge className={`${statusColors[selectedTrip.status]} text-white`}>
                          {selectedTrip.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Origin:</span>
                        <span className="text-white text-right max-w-[200px] truncate">
                          {selectedTrip.origin_address || 'N/A'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-400">Device & Driver</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Device UID:</span>
                        <span className="text-white">{selectedTrip.device_uid || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Driver:</span>
                        <span className="text-white">{selectedTrip.driver_name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Vehicle:</span>
                        <span className="text-white">{selectedTrip.vehicle_number || 'N/A'}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-400">Temperature Requirements</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Min Temp:</span>
                        <span className="text-blue-400">{selectedTrip.temp_min}°C</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Max Temp:</span>
                        <span className="text-red-400">{selectedTrip.temp_max}°C</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-400">Timeline</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Planned Start:</span>
                        <span className="text-white text-sm">{formatDate(selectedTrip.planned_start)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Actual Start:</span>
                        <span className="text-white text-sm">{formatDate(selectedTrip.actual_start)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Actual End:</span>
                        <span className="text-white text-sm">{formatDate(selectedTrip.actual_end)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
